import { useCallback } from 'react';
import { IconButton, Tooltip, makeStyles } from '@material-ui/core';
import { darken, lighten } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import { useSidebarOpenState } from '@backstage/core-components';

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  // In dark mode, use background.paper (lighter) for elevated button; in light mode use white
  const buttonBackgroundColor = isDark
    ? theme.palette.background.paper
    : '#ffffff';
  const baseBackgroundColor = isDark
    ? theme.palette.background.paper
    : theme.palette.navigation.background;

  // Lighten in dark mode, use light gray in light mode (since background is white)
  const hoverBackgroundColor = isDark
    ? lighten(baseBackgroundColor, 0.15)
    : '#f5f5f5';

  // Border color with more contrast than hover background
  const borderColor = isDark
    ? lighten(baseBackgroundColor, 0.35)
    : darken(baseBackgroundColor, 0.25);

  return {
    button: {
      width: 24,
      height: 24,
      backgroundColor: buttonBackgroundColor,
      border: `1px solid ${borderColor}`,
      color: theme.palette.navigation.color,
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
      },
      '& .MuiSvgIcon-root': {
        fontSize: '0.875rem',
        opacity: 0.7,
      },
    },
  };
});

/**
 * Custom sidebar toggle component to expand or collapse the Backstage sidebar
 */
export const SidebarToggle = () => {
  const classes = useStyles();
  const { isOpen, setOpen } = useSidebarOpenState();

  const handleToggle = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen, setOpen]);

  const tooltipTitle = `${isOpen ? 'Collapse' : 'Expand'} sidebar`;

  return (
    <Tooltip title={tooltipTitle}>
      <IconButton
        onClick={handleToggle}
        className={classes.button}
        size="small"
        aria-label={tooltipTitle}
      >
        <MenuIcon data-testid="MenuIcon" />
      </IconButton>
    </Tooltip>
  );
};
